/** Supported promotion types (extend when backend adds types). */
export const PROMOTION_TYPE_FREE_BET = 'free_bet' as const

export type PromotionType = typeof PROMOTION_TYPE_FREE_BET

export const SUPPORTED_PROMOTION_TYPES: PromotionType[] = [PROMOTION_TYPE_FREE_BET]

export interface ActivePromotion {
  type: PromotionType
  id: string
  token: string
  stake: number
  currency: string
  expiresAt?: string
  eligibleGameSlugs?: string[]
  singleLineOnly?: boolean
  label?: string
}

export interface PromotionPlacePayload {
  type: PromotionType
  id: string
  token: string
  stake: number
}

function readPromotionType(raw: Record<string, unknown>, fallback: PromotionType): PromotionType {
  const type = String(raw.type ?? raw.promotion_type ?? fallback).trim()
  if ((SUPPORTED_PROMOTION_TYPES as string[]).includes(type)) {
    return type as PromotionType
  }
  return fallback
}

/** Normalize API / iframe payload into an active promotion (type-aware). */
export function normalizePromotionConfig(
  raw: unknown,
  options?: { defaultType?: PromotionType }
): ActivePromotion | null {
  if (!raw || typeof raw !== 'object') return null
  const data = raw as Record<string, unknown>
  const defaultType = options?.defaultType ?? PROMOTION_TYPE_FREE_BET
  const type = readPromotionType(data, defaultType)

  const id = typeof data.id === 'string' ? data.id.trim() : ''
  const token = typeof data.token === 'string' ? data.token.trim() : ''
  const stake = Number(data.stake)
  const currency = typeof data.currency === 'string' ? data.currency.trim() : ''
  if (!id || !token || !Number.isFinite(stake) || stake <= 0 || !currency) {
    return null
  }

  const config: ActivePromotion = {
    type,
    id,
    token,
    stake,
    currency,
  }

  const expiresAt = data.expiresAt ?? data.expires_at
  if (typeof expiresAt === 'string' && expiresAt.trim()) {
    config.expiresAt = expiresAt.trim()
  }

  const slugs = data.eligibleGameSlugs ?? data.eligible_game_slugs
  if (Array.isArray(slugs)) {
    config.eligibleGameSlugs = slugs
      .map((slug) => String(slug).trim().toLowerCase())
      .filter(Boolean)
  }

  const singleLine = data.singleLineOnly ?? data.single_line_only
  if (singleLine === true) {
    config.singleLineOnly = true
  }

  const label = data.label
  if (typeof label === 'string' && label.trim()) {
    config.label = label.trim()
  }

  return config
}

export function isPromotionExpired(promotion: ActivePromotion): boolean {
  if (!promotion.expiresAt) return false
  const expires = Date.parse(promotion.expiresAt)
  return Number.isFinite(expires) && Date.now() >= expires
}

export function promotionBannerLabel(promotion: ActivePromotion): string {
  if (promotion.label?.trim()) {
    return promotion.label.trim()
  }
  switch (promotion.type) {
    case PROMOTION_TYPE_FREE_BET:
      return `Free bet — ${promotion.stake.toFixed(2)} ${promotion.currency}`
    default:
      return `Promotion — ${promotion.stake.toFixed(2)} ${promotion.currency}`
  }
}

export function promotionSlipErrorMessage(
  promotion: ActivePromotion,
  code: string,
  lineCount = 1
): string {
  switch (code) {
    case 'currency_mismatch':
      return 'Promotion currency does not match your session.'
    case 'single_line':
      return promotion.type === PROMOTION_TYPE_FREE_BET
        ? 'This free bet allows only one bet line.'
        : 'This promotion allows only one bet line.'
    case 'stake_mismatch': {
      const amount = promotion.stake.toFixed(2)
      const isFreeBet = promotion.type === PROMOTION_TYPE_FREE_BET
      if (lineCount > 1) {
        return isFreeBet
          ? `Free bet total across all lines must be ${amount}.`
          : `Promotion total across all lines must be ${amount}.`
      }
      return isFreeBet
        ? `Free bet stake must be ${amount}.`
        : `Promotion stake must be ${amount}.`
    }
    case 'game_ineligible':
      return promotion.type === PROMOTION_TYPE_FREE_BET
        ? 'One or more selections are not eligible for this free bet.'
        : 'One or more selections are not eligible for this promotion.'
    default:
      return 'This promotion cannot be applied to the current betslip.'
  }
}
